/**
 * AiSmartAddModal — Natural-language family-tree editor.
 *
 * Flow:
 *   1. User types: "Add my mother's brother Rajesh, his wife Suma, and their
 *      two children Arun (son) and Priya (daughter)"
 *   2. We POST to /family-trees/:id/ai-smart-add → GPT-5.2 → structured diff.
 *   3. We display the proposed persons + relationships for review.
 *   4. User confirms → we commit via existing /members and /relationships
 *      endpoints (one request per entity, in dependency order).
 *   5. onSaved(count) is fired so the parent tree can reload.
 *
 * We never commit automatically. Every change must be approved.
 */
import { useState } from "react";
import { X, Sparkles, Loader2, UserPlus, Link2, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  saffronBg: "#FFF7ED",
  parchment: "#FFFBF4",
  line: "rgba(194,65,12,0.15)",
};

const EXAMPLES = [
  "Add my mother's brother Rajesh and his wife Suma. They have two children: Arun (son) and Priya (daughter).",
  "Add Grandpa Thomas's younger sister Mary, who was married to Varghese. They had no children.",
  "Add my cousin Anu, daughter of my paternal uncle Kumaran.",
];

export default function AiSmartAddModal({ treeId, members, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [proposed, setProposed] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [anchorMap, setAnchorMap] = useState({}); // temp_anchor_id → existing member_id

  const parse = async () => {
    if (!text.trim() || text.trim().length < 3) {
      toast.error("Please describe who you want to add.");
      return;
    }
    setParsing(true);
    setProposed(null);
    setAnchorMap({});
    try {
      const r = await http.post(`/family-trees/${treeId}/ai-smart-add`, { text: text.trim() });
      const p = r.data?.proposed;
      if (!p || (!p.persons?.length && !p.anchors?.length)) {
        toast.error("I couldn't find any family in that description. Try rephrasing with specific names.");
      } else {
        setProposed(p);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI couldn't parse that. Try again or add manually.");
    } finally {
      setParsing(false);
    }
  };

  const commit = async () => {
    if (!proposed) return;
    // Require every anchor to be mapped to an existing member.
    const unmapped = (proposed.anchors || []).filter((a) => !anchorMap[a.id]);
    if (unmapped.length > 0) {
      toast.error(`Please pick the real family member for: ${unmapped.map((a) => a.label).join(", ")}`);
      return;
    }

    setCommitting(true);
    try {
      // 1. Create persons in order, build temp_id → real_id map.
      const idMap = { ...anchorMap }; // anchor temp_ids already map to real member_ids
      for (const p of proposed.persons || []) {
        const payload = {
          first_name: p.first_name,
          last_name: p.last_name || null,
          nickname: p.nickname || null,
          gender: p.gender === "unknown" ? null : p.gender,
          birth_date: p.birth_date || (p.birth_year ? `${p.birth_year}-01-01` : null),
          birth_date_is_year_only: !!(p.birth_year && !p.birth_date),
          notes: p.notes || null,
        };
        const r = await http.post(`/family-trees/${treeId}/members`, payload);
        idMap[p.id] = r.data?.id;
      }

      // 2. Add relationships.
      let addedRels = 0;
      for (const rel of proposed.relationships || []) {
        const a = idMap[rel.from];
        const b = idMap[rel.to];
        if (!a || !b) continue;
        try {
          await http.post(`/family-trees/${treeId}/relationships`, {
            type: rel.type,
            a,
            b,
          });
          addedRels += 1;
        } catch (e) {
          // Non-fatal — one bad edge shouldn't kill the whole commit.
          console.warn("rel commit failed", rel, e);
        }
      }

      toast.success(`Added ${proposed.persons.length} ${proposed.persons.length === 1 ? "person" : "people"} and ${addedRels} ${addedRels === 1 ? "connection" : "connections"}.`);
      onSaved?.(proposed.persons.length);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save everything. Some changes may have been applied.");
      // Even on partial failure, parent should reload to show what was saved.
      onSaved?.(0);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(67,26,4,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="ai-smart-add-modal"
    >
      <div
        className="w-full max-w-2xl rounded-3xl overflow-hidden my-8"
        style={{ background: P.parchment, boxShadow: "0 40px 80px -20px rgba(67,26,4,0.5)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ background: `linear-gradient(135deg, ${P.saffronBg} 0%, ${P.parchment} 100%)`, borderBottom: `1px solid ${P.line}` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: P.saffron, color: "#fff" }}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-lg" style={{ color: P.ink }}>AI Smart Add</div>
              <div className="text-xs" style={{ color: P.inkSoft }}>Describe your family in plain English.</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/60 transition-colors flex items-center justify-center" style={{ color: P.inkSoft }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!proposed && (
            <>
              <textarea
                data-testid="ai-smart-add-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Add my mother's brother Rajesh and his wife Suma. They have two children, Arun (son, born 2015) and Priya (daughter, 2018)."
                rows={5}
                className="w-full rounded-2xl p-4 text-sm resize-y outline-none focus:ring-2"
                style={{ background: "#fff", border: `1px solid ${P.line}`, color: P.ink }}
                disabled={parsing}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setText(ex)}
                    data-testid={`ai-smart-add-example-${i}`}
                    className="text-[11px] rounded-full px-2.5 py-1 transition-colors"
                    style={{ background: "rgba(194,65,12,0.08)", color: P.saffron }}
                  >
                    Try: {ex.slice(0, 46)}…
                  </button>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button onClick={onClose} className="wofy-btn-ghost text-sm" data-testid="ai-smart-add-cancel">Cancel</button>
                <button
                  onClick={parse}
                  disabled={parsing || !text.trim()}
                  data-testid="ai-smart-add-parse"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                  style={{ background: P.saffron, color: "#fff" }}
                >
                  {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {parsing ? "Reading your description…" : "Draft the branch"}
                </button>
              </div>
            </>
          )}

          {proposed && (
            <>
              {proposed.summary && (
                <div className="rounded-2xl p-4 mb-4 text-sm" style={{ background: P.saffronBg, color: P.ink, border: `1px solid ${P.line}` }}>
                  <span className="font-semibold">AI understood:</span> {proposed.summary}
                </div>
              )}

              {proposed.anchors?.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold tracking-widest uppercase mb-2" style={{ color: P.inkSoft }}>
                    Which real family member does this refer to?
                  </div>
                  {proposed.anchors.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 mb-2" data-testid={`ai-smart-add-anchor-${a.id}`}>
                      <div
                        className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
                        style={{ background: "rgba(123,66,22,0.1)", color: P.ink }}
                      >
                        "{a.label}"
                      </div>
                      <span style={{ color: P.inkSoft }}>→</span>
                      <select
                        value={anchorMap[a.id] || ""}
                        onChange={(e) => setAnchorMap((prev) => ({ ...prev, [a.id]: e.target.value }))}
                        className="flex-1 rounded-xl px-3 py-2 text-sm outline-none"
                        style={{ background: "#fff", border: `1px solid ${P.line}`, color: P.ink }}
                      >
                        <option value="">Select member…</option>
                        {(members || []).map((m) => (
                          <option key={m.id} value={m.id}>
                            {[m.first_name, m.last_name].filter(Boolean).join(" ")}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {proposed.persons?.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-4 h-4" style={{ color: P.saffron }} />
                    <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: P.inkSoft }}>
                      New people ({proposed.persons.length})
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {proposed.persons.map((p) => (
                      <li
                        key={p.id}
                        className="rounded-xl px-4 py-3 text-sm"
                        style={{ background: "#fff", border: `1px solid ${P.line}` }}
                        data-testid={`ai-smart-add-person-${p.id}`}
                      >
                        <div className="font-semibold" style={{ color: P.ink }}>
                          {[p.first_name, p.last_name].filter(Boolean).join(" ")}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: P.inkSoft }}>
                          {[p.gender !== "unknown" ? p.gender : null, p.birth_year ? `born ${p.birth_year}` : null, p.notes]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {proposed.relationships?.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4" style={{ color: P.saffron }} />
                    <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: P.inkSoft }}>
                      New connections ({proposed.relationships.length})
                    </span>
                  </div>
                  <ul className="space-y-1 text-xs" style={{ color: P.ink }}>
                    {proposed.relationships.map((r, i) => {
                      const nameFor = (id) => {
                        const a = proposed.anchors.find((x) => x.id === id);
                        if (a) return `"${a.label}"`;
                        const p = proposed.persons.find((x) => x.id === id);
                        return p ? [p.first_name, p.last_name].filter(Boolean).join(" ") : id;
                      };
                      return (
                        <li key={i}>• {nameFor(r.from)} is the <strong>{r.type}</strong> of {nameFor(r.to)}</li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div
                className="flex items-start gap-2 rounded-xl p-3 mb-5 text-xs"
                style={{ background: "rgba(194,65,12,0.06)", color: P.inkSoft }}
              >
                <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: P.saffron, marginTop: 1 }} />
                <div>
                  Nothing is saved yet. Review carefully, then press Confirm. You can always edit or delete members afterwards.
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => { setProposed(null); }}
                  className="wofy-btn-ghost text-sm"
                  data-testid="ai-smart-add-rewrite"
                >
                  ← Edit description
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
                  <button
                    onClick={commit}
                    disabled={committing}
                    data-testid="ai-smart-add-commit"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                    style={{ background: P.saffron, color: "#fff" }}
                  >
                    {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {committing ? "Saving…" : `Confirm & add ${proposed.persons.length}`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
